import React, { useState, useEffect } from 'react';
import { Plus, Upload } from 'lucide-react';
import { OptimizedImage } from '../components/ui/optimized-image';

const FamillesMembre = () => {
  const [familles, setFamilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_file: null,
    image_preview: null
  });

  useEffect(() => {
    loadFamilles();
  }, []);

  const loadFamilles = async () => {
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/familles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFamilles(data);
      }
    } catch (error) {
      console.error('Erreur chargement familles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image_file: file,
        image_preview: URL.createObjectURL(file)
      });
    }
  };

  const handleProposal = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken');
      
      // Upload l'image d'abord si une nouvelle image est sélectionnée
      let uploaded_image_path = null;
      if (formData.image_file) {
        const imageFormData = new FormData();
        imageFormData.append('file', formData.image_file);
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/upload-image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: imageFormData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          uploaded_image_path = uploadData.file_url || uploadData.url;
        }
      }
      
      // Envoyer la proposition
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/familles/propositions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          famille_id: selectedFamille?.id || null,
          name: formData.name,
          description: formData.description,
          image_url: uploaded_image_path || (selectedFamille?.image_url || null)
        })
      });

      if (response.ok) {
        alert('Proposition envoyée avec succès! Elle sera examinée par un administrateur.');
        setShowProposalForm(false);
        setFormData({ name: '', description: '', image_file: null, image_preview: null });
        setSelectedFamille(null);
      }
    } catch (error) {
      console.error('Erreur envoi proposition:', error);
      alert('Erreur lors de l\'envoi de la proposition');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (famille) => {
    setSelectedFamille(famille);
    setFormData({
      name: famille.name,
      description: famille.description || '',
      image_file: null,
      image_preview: famille.image_url ? `${process.env.REACT_APP_BACKEND_URL}${famille.image_url}` : null
    });
    setShowProposalForm(true);
    // Scroll vers le haut pour voir le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewProposal = () => {
    setSelectedFamille(null);
    setFormData({ name: '', description: '', image_file: null, image_preview: null });
    setShowProposalForm(true);
    // Scroll vers le haut pour voir le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <div className="p-6">Chargement...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Familles et Blasons
          </h2>
          <p className="text-gray-600">Consultez les familles lignagères et proposez des ajouts</p>
        </div>
        <button
          onClick={handleNewProposal}
          className="flex items-center px-4 py-2 text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Proposer un ajout
        </button>
      </div>

      {/* Formulaire de proposition */}
      {showProposalForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)' }}>
            {selectedFamille ? 'Proposer une modification' : 'Proposer un ajout'}
          </h3>
          <form onSubmit={handleProposal}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom de la famille</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="4"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image du blason</label>
                {formData.image_preview && (
                  <div className="mb-2">
                    <img src={formData.image_preview} alt="Aperçu" className="w-32 h-32 object-contain border rounded" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG, GIF</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  {uploading ? 'Envoi en cours...' : 'Envoyer la proposition'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowProposalForm(false);
                    setSelectedFamille(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Votre proposition sera soumise à validation par un administrateur avant publication.
              </p>
            </div>
          </form>
        </div>
      )}

      {/* Liste des familles en TABLEAU */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-24">
                Blason
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider" style={{ width: '250px' }}>
                Nom de famille
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-48">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {familles.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                  Aucune famille enregistrée pour le moment.
                  <br />
                  <button
                    onClick={handleNewProposal}
                    className="mt-4 px-4 py-2 text-white rounded-lg"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    Proposer la première famille
                  </button>
                </td>
              </tr>
            ) : (
              familles.map((famille) => (
                <tr key={famille.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 w-24">
                    {famille.image_url ? (
                      <OptimizedImage
                        src={`${process.env.REACT_APP_BACKEND_URL}${famille.image_url}`}
                        alt={famille.name}
                        className="w-16 h-16 object-contain"
                        useThumbnail={true}
                        placeholder="skeleton"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                        Pas d'image
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" style={{ width: '250px' }}>
                    <div className="text-sm font-medium text-gray-900 break-words">{famille.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 whitespace-normal">
                      {famille.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm w-48">
                    <button
                      onClick={() => handleEdit(famille)}
                      className="px-3 py-1 text-white rounded hover:opacity-80"
                      style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    >
                      Proposer une modification
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FamillesMembre;
